/**
 * QuickEmployeeAddDialog - Dialog to quickly create a new employee
 * Adapted from Thaklis app to use ThaklisApiService
 * Includes passport upload + OCR extraction feature
 */
import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Alert, CircularProgress, IconButton, LinearProgress
} from '@mui/material';
import {
  Close as CloseIcon, PersonAdd as PersonAddIcon,
  CloudUpload as CloudUploadIcon, DocumentScanner as DocumentScannerIcon
} from '@mui/icons-material';
import ThaklisApiService from '../../../services/thaklis-api.service';

const GENDERS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' }
];

const EXTRACT_MAX_RETRIES = 3;
const EXTRACT_RETRY_DELAY = 2000;

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

const QuickEmployeeAddDialog = ({ open, onClose, onEmployeeAdded, organizationId }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Passport upload + extract state
  const [passportFile, setPassportFile] = useState(null);
  const [passportPreview, setPassportPreview] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractStatus, setExtractStatus] = useState('');
  const [extractError, setExtractError] = useState('');
  const [extracted, setExtracted] = useState(false);
  const passportInputRef = useRef(null);

  const [formData, setFormData] = useState({
    firstNameEn: '', middleNameEn: '', lastNameEn: '',
    mobileNumber: '', dob: '', gender: '',
    passportNumber: '', passportIssue: '', passportExpiry: '',
    nationality: ''
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => { const n = { ...prev }; delete n[name]; return n; });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstNameEn?.trim()) newErrors.firstNameEn = 'First name is required';
    if (!formData.lastNameEn?.trim()) newErrors.lastNameEn = 'Last name is required';
    if (!formData.mobileNumber?.trim()) newErrors.mobileNumber = 'Mobile number is required';
    if (!formData.dob) newErrors.dob = 'Date of birth is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.passportNumber?.trim()) newErrors.passportNumber = 'Passport number is required';
    if (!formData.passportExpiry) newErrors.passportExpiry = 'Passport expiry date is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // === Passport File Selection ===
  const handlePassportFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setExtractError('File size exceeds the 10MB limit.');
      return;
    }

    setPassportFile(file);
    setExtractError('');
    setExtracted(false);

    // Generate preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPassportPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPassportPreview(null);
    }

    // Auto-extract immediately after file selection
    extractFromFile(file);
  };

  // === Passport OCR Extraction (auto-triggered on file upload) ===
  const extractFromFile = async (file) => {
    if (!file) return;

    setExtracting(true);
    setExtractError('');
    setExtractStatus('Uploading passport for extraction...');

    const extractFormData = new FormData();
    extractFormData.append('file', file);
    extractFormData.append('documentType', 'PASSPORT_COPY');

    let lastError = null;
    for (let attempt = 1; attempt <= EXTRACT_MAX_RETRIES; attempt++) {
      try {
        setExtractStatus(`Extracting data (attempt ${attempt}/${EXTRACT_MAX_RETRIES})...`);

        const response = await ThaklisApiService.post(
          'v1/documents/processing/extract',
          extractFormData,
          { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 }
        );

        const extractedData = response.data?.data || response.data;

        if (extractedData) {
          autoFillFromExtraction(extractedData);
          setExtracted(true);
          setExtractStatus('Data extracted successfully!');
          setExtracting(false);
          return;
        }

        lastError = new Error('No data returned from extraction');
      } catch (err) {
        console.error(`Extraction attempt ${attempt} failed:`, err);
        lastError = err;

        if (attempt < EXTRACT_MAX_RETRIES) {
          setExtractStatus(`Attempt ${attempt} failed. Retrying in ${EXTRACT_RETRY_DELAY / 1000}s...`);
          await sleep(EXTRACT_RETRY_DELAY);
        }
      }
    }

    setExtracting(false);
    setExtractStatus('');
    setExtractError(
      lastError?.response?.data?.message ||
      lastError?.message ||
      'Failed to extract data after multiple attempts.'
    );
  };

  // === Auto-fill form from extracted data ===
  const autoFillFromExtraction = (data) => {
    const updates = {};

    // Full name → split into first/last
    if (data.fullName) {
      const nameParts = data.fullName.trim().split(/\s+/);
      if (nameParts.length >= 2) {
        updates.firstNameEn = nameParts[0];
        updates.lastNameEn = nameParts[nameParts.length - 1];
        if (nameParts.length > 2) {
          updates.middleNameEn = nameParts.slice(1, -1).join(' ');
        }
      } else {
        updates.firstNameEn = data.fullName;
      }
    }

    // Individual name fields (override fullName split if present)
    if (data.firstName) updates.firstNameEn = data.firstName;
    if (data.lastName) updates.lastNameEn = data.lastName;
    if (data.middleName) updates.middleNameEn = data.middleName;

    // Passport number
    if (data.passportNumber || data.documentNumber) {
      updates.passportNumber = data.passportNumber || data.documentNumber;
    }

    // Dates - format to YYYY-MM-DD for input[type=date]
    const formatDate = (dateStr) => {
      if (!dateStr) return '';
      try {
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return '';
        return d.toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    if (data.expiryDate) updates.passportExpiry = formatDate(data.expiryDate);
    if (data.issueDate) updates.passportIssue = formatDate(data.issueDate);
    if (data.dateOfBirth) updates.dob = formatDate(data.dateOfBirth);

    // Nationality
    if (data.nationality) updates.nationality = data.nationality;

    // Gender
    if (data.gender) {
      const g = data.gender.toLowerCase();
      if (g === 'male' || g === 'm') updates.gender = 'male';
      else if (g === 'female' || g === 'f') updates.gender = 'female';
    }

    // Only update non-empty fields (don't overwrite user's manual entries with empty values)
    setFormData(prev => {
      const merged = { ...prev };
      for (const [key, value] of Object.entries(updates)) {
        if (value) merged[key] = value;
      }
      return merged;
    });

    // Clear validation errors for auto-filled fields
    setErrors(prev => {
      const updated = { ...prev };
      for (const key of Object.keys(updates)) {
        if (updates[key]) delete updated[key];
      }
      return updated;
    });
  };

  // === Upload passport as employee document after creation ===
  const uploadPassportDocument = async (employeeUid) => {
    if (!passportFile || !employeeUid) return;

    try {
      // Step 1: Upload file
      const uploadFormData = new FormData();
      uploadFormData.append('file', passportFile);
      uploadFormData.append('documentType', 'passport_copy');
      uploadFormData.append('employeeUid', employeeUid);
      uploadFormData.append('fileName', passportFile.name);

      const uploadResp = await ThaklisApiService.post('v1/files/upload', uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadResult = uploadResp.data || {};
      const filePath = uploadResult.filePath || uploadResult.data?.filePath || uploadResult.path || uploadResult.data?.path;

      if (!filePath) {
        console.error('Failed to get filePath from passport upload');
        return;
      }

      // Step 2: Save document via bulk API
      const bulkPayload = {
        documentType: 'passport_copy',
        replaceExisting: false,
        documents: [{ filePath, fileName: passportFile.name, fileSize: passportFile.size }]
      };

      await ThaklisApiService.post(`v1/employee-documents/${employeeUid}/bulk`, bulkPayload);
    } catch (err) {
      console.error('Error uploading passport document:', err);
      // Non-fatal - employee was already created successfully
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setError('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload = {
        personalInfo: {
          firstName: formData.firstNameEn,
          middleName: formData.middleNameEn || '',
          lastName: formData.lastNameEn,
          mobileNumber: formData.mobileNumber,
          dateOfBirth: formData.dob ? new Date(formData.dob).toISOString() : null,
          gender: formData.gender,
          passportNumber: formData.passportNumber,
          passportExpiry: formData.passportExpiry ? new Date(formData.passportExpiry).toISOString() : null,
          passportIssue: formData.passportIssue ? new Date(formData.passportIssue).toISOString() : null,
          nationality: formData.nationality || null,
          emiratesId: '', emiratesIdIssueDate: null, emiratesIdExpiryDate: null,
          visaNumber: '', visaType: '', visaIssueDate: null, visaExpiryDate: null
        },
        employmentInfo: {
          department: null, position: null,
          startDate: new Date().toISOString().split('T')[0],
          employeeId: '', status: 'ACTIVE'
        },
        contactInfo: {
          email: '', requiresLogin: false, accessLevel: 'CUSTOMER_EMPLOYEE'
        },
        uid: null,
        organizationId: organizationId
      };

      const response = await ThaklisApiService.post('v1/employees', payload);
      const createdEmployee = response.data?.data || response.data;

      setSuccess(true);

      // Upload passport document if file was selected
      if (passportFile && createdEmployee?.uid) {
        await uploadPassportDocument(createdEmployee.uid);
      }

      const normalizedEmployee = {
        ...createdEmployee,
        firstNameEn: createdEmployee.firstNameEn || createdEmployee.firstName,
        middleNameEn: createdEmployee.middleNameEn || createdEmployee.middleName,
        lastNameEn: createdEmployee.lastNameEn || createdEmployee.lastName,
        nameEn: createdEmployee.nameEn || createdEmployee.fullName ||
          [createdEmployee.firstNameEn || createdEmployee.firstName,
           createdEmployee.middleNameEn || createdEmployee.middleName,
           createdEmployee.lastNameEn || createdEmployee.lastName].filter(Boolean).join(' '),
        mobileNumber: createdEmployee.mobileNumber || createdEmployee.mobile,
        email: createdEmployee.email || '',
        uid: createdEmployee.uid,
        employeeId: createdEmployee.employeeId || createdEmployee.id
      };

      setTimeout(() => {
        if (onEmployeeAdded) onEmployeeAdded(normalizedEmployee);
        handleClose();
      }, 1000);
    } catch (err) {
      console.error('Error creating employee:', err);
      setError(err.response?.data?.message || err.message || 'Failed to create employee');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        firstNameEn: '', middleNameEn: '', lastNameEn: '',
        mobileNumber: '', dob: '', gender: '',
        passportNumber: '', passportIssue: '', passportExpiry: '',
        nationality: ''
      });
      setErrors({});
      setError('');
      setSuccess(false);
      setPassportFile(null);
      setPassportPreview(null);
      setExtracting(false);
      setExtractStatus('');
      setExtractError('');
      setExtracted(false);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0', pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" component="span">Quick Add Employee</Typography>
        </Box>
        <IconButton edge="end" onClick={handleClose} disabled={loading}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>Employee created successfully!</Alert>}

        {/* Passport Upload + Extract Section */}
        <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <DocumentScannerIcon fontSize="small" />
            Passport Upload & Extract
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a passport image or PDF to automatically extract employee details.
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            {/* File input + preview */}
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={extracting ? <CircularProgress size={18} /> : <CloudUploadIcon />}
                disabled={extracting || loading}
                fullWidth
                color={extracted ? 'success' : 'primary'}
                sx={{ mb: 1, color: extracting || loading ? undefined : 'primary.main' }}
              >
                <Typography component="span" sx={{ fontWeight: 500 }}>
                  {extracting ? 'Extracting...' : passportFile ? 'Change Passport File' : 'Upload Passport'}
                </Typography>
                <input
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={handlePassportFileSelect}
                  ref={passportInputRef}
                />
              </Button>

              {passportFile && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    {passportFile.name} ({(passportFile.size / 1024).toFixed(1)} KB)
                  </Typography>
                  {extracted && (
                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                      — Data extracted
                    </Typography>
                  )}
                </Box>
              )}

              {passportPreview && (
                <Box sx={{ mt: 1, mb: 1, textAlign: 'center' }}>
                  <img
                    src={passportPreview}
                    alt="Passport preview"
                    style={{ maxWidth: '100%', maxHeight: 150, borderRadius: 4, border: '1px solid #ddd' }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* Extract progress/status */}
          {extracting && (
            <Box sx={{ mt: 1.5 }}>
              <LinearProgress sx={{ mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary">{extractStatus}</Typography>
            </Box>
          )}

          {extractError && (
            <Alert severity="error" sx={{ mt: 1.5 }} variant="outlined" onClose={() => setExtractError('')}>
              {extractError}
            </Alert>
          )}
        </Box>

        {/* Form Fields */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="subtitle1" color="primary" sx={{ fontWeight: 600, mb: 1 }}>Personal Information</Typography>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="First Name (English)" name="firstNameEn" value={formData.firstNameEn}
              onChange={handleChange} required error={!!errors.firstNameEn} helperText={errors.firstNameEn} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Middle Name (English)" name="middleNameEn" value={formData.middleNameEn}
              onChange={handleChange} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Last Name (English)" name="lastNameEn" value={formData.lastNameEn}
              onChange={handleChange} required error={!!errors.lastNameEn} helperText={errors.lastNameEn} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Mobile Number" name="mobileNumber" value={formData.mobileNumber}
              onChange={handleChange} required error={!!errors.mobileNumber} helperText={errors.mobileNumber} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Date of Birth" name="dob" type="date" value={formData.dob}
              onChange={handleChange} required error={!!errors.dob} helperText={errors.dob}
              InputLabelProps={{ shrink: true }} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small" required error={!!errors.gender} disabled={loading}>
              <InputLabel>Gender</InputLabel>
              <Select name="gender" value={formData.gender} onChange={handleChange} label="Gender">
                {GENDERS.map(g => <MenuItem key={g.value} value={g.value}>{g.label}</MenuItem>)}
              </Select>
              {errors.gender && <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>{errors.gender}</Typography>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Nationality" name="nationality" value={formData.nationality}
              onChange={handleChange} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Passport Number" name="passportNumber" value={formData.passportNumber}
              onChange={handleChange} required error={!!errors.passportNumber} helperText={errors.passportNumber} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Passport Issue Date" name="passportIssue" type="date" value={formData.passportIssue}
              onChange={handleChange} InputLabelProps={{ shrink: true }} disabled={loading} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Passport Expiry Date" name="passportExpiry" type="date" value={formData.passportExpiry}
              onChange={handleChange} required error={!!errors.passportExpiry} helperText={errors.passportExpiry}
              InputLabelProps={{ shrink: true }} disabled={loading} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleClose} disabled={loading} variant="outlined">Cancel</Button>
        <Button onClick={handleSubmit} disabled={loading} variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <PersonAddIcon />}>
          {loading ? 'Creating...' : 'Create Employee'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickEmployeeAddDialog;
